import { Injectable } from '@angular/core';
import { environment } from '@env';

type InsightsClient = import('@microsoft/applicationinsights-web').ApplicationInsights;

@Injectable({
   providedIn: 'root',
})
export class ApplicationInsightsService {
   private clientPromise: Promise<InsightsClient | null> | null = null;
   public isEnabled: boolean = environment.appInsights.enabled;

   constructor() {
      void this.client();
   }

   initialize(): void {}

   logException(exception: any, properties?: { [key: string]: any }, severityLevel = 3): void {
      void this.client().then((appInsights) => {
         appInsights?.trackException({ exception, severityLevel, properties });
      });
   }

   setUserContext(userId: string): void {
      if (!userId) {
         return;
      }

      void this.client().then((appInsights) => {
         if (!appInsights) {
            return;
         }
         try {
            appInsights.setAuthenticatedUserContext(userId);
         } catch (error) {}
      });
   }

   clearUserContext(): void {
      void this.client().then((appInsights) => {
         if (!appInsights) {
            return;
         }
         try {
            appInsights.clearAuthenticatedUserContext();
         } catch (error) {}
      });
   }

   private client(): Promise<InsightsClient | null> {
      this.clientPromise ??= this.createClient();
      return this.clientPromise;
   }

   private async createClient(): Promise<InsightsClient | null> {
      if (!environment.appInsights.instrumentationKey || !environment.appInsights.enabled) {
         return null;
      }

      const { ApplicationInsights } = await import('@microsoft/applicationinsights-web');
      const appInsights = new ApplicationInsights({
         config: {
            instrumentationKey: environment.appInsights.instrumentationKey,
            enableAutoRouteTracking: false,
            disableAjaxTracking: true,
            disableFetchTracking: true,
            disableCorrelationHeaders: true,
            disableExceptionTracking: false,
            enableUnhandledPromiseRejectionTracking: true,
         },
      });

      appInsights.addTelemetryInitializer((envelope) => {
         return envelope.baseType === 'ExceptionData';
      });
      appInsights.loadAppInsights();
      appInsights.context.application.ver = environment.version;
      return appInsights;
   }
}
